import React from 'react';
import StatusCodeFilter from './StatusCodeFilter';
import TimePeriodFilter from './TimePeriodFilter';
import ServiceFilter from './ServiceFilter';
import useMonitorFilters from '../../hooks/useMonitorFilters';

/**
 * Monitor Filters Container Component
 * Combines status code and time period filters with apply/reset functionality
 * 
 * @param {Object} props
 * @param {Function} props.onApplyFilters - Callback when filters are applied
 * @param {Object} props.initialFilters - Initial filter values
 * @param {boolean} props.showTimePeriod - Whether to show time period filter (default: true)
 * @param {boolean} props.compact - Compact mode for main monitor page (default: false)
 */
const MonitorFilters = ({ 
  onApplyFilters, 
  initialFilters = {},
  showTimePeriod = true,
  compact = false
}) => {
  const {
    filters,
    setStatusFilter,
    setTimeFilter,
    setServiceFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
    getFilterDescription
  } = useMonitorFilters(initialFilters);

  const handleApply = () => {
    onApplyFilters(filters);
  };

  const handleReset = () => {
    resetFilters();
    onApplyFilters({
      statusCodes: { type: 'all', codes: [] },
      timePeriod: { type: 'all', startDate: null, endDate: null },
      service: 'all'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          🔍 Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {activeFilterCount}
            </span>
          )}
        </h3>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Filter Controls */}
      <div className={`grid gap-4 ${
        compact
          ? 'grid-cols-1 sm:grid-cols-3'
          : showTimePeriod
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          : 'grid-cols-1 sm:grid-cols-2'
      }`}>
        {/* Service Filter */}
        <ServiceFilter
          value={filters.service}
          onChange={setServiceFilter}
        />

        {/* Status Code Filter */}
        <StatusCodeFilter
          value={filters.statusCodes}
          onChange={setStatusFilter}
        />

        {/* Time Period Filter */}
        {showTimePeriod && (
          <TimePeriodFilter
            value={filters.timePeriod}
            onChange={setTimeFilter}
          />
        )}
      </div>

      {/* Active Filter Description */}
      {hasActiveFilters && getFilterDescription && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            <span className="font-medium">Active filters:</span> {getFilterDescription}
          </p>
        </div>
      )}

      {/* Apply Button */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={handleApply}
          className="flex-1 sm:flex-none inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Apply Filters
        </button>
        
        {!compact && hasActiveFilters && (
          <button
            onClick={handleReset}
            className="flex-1 sm:flex-none inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default MonitorFilters;